import type { UserDTO } from "@chatsystem/common/dtos";

export type ApiSessionError = Error & {
  status?: number;
  reference?: string;
};

export type ApiSessionOperations = {
  fetchUser: () => Promise<UserDTO>;
  refresh: () => Promise<boolean>;
  exchange: () => Promise<void>;
};

export type ApiSessionRecovery = "refresh" | "exchange" | null;

export type ApiSessionResolution = {
  user: UserDTO;
  recovery: ApiSessionRecovery;
};

export type AccountGateState = "loading" | "content" | "error" | "redirect";

const isUnauthorized = (error: unknown): boolean =>
  (error as ApiSessionError | undefined)?.status === 401;

/**
 * Resolve the API user behind an authenticated NextAuth session.
 *
 * The API access token may be valid, refreshable, or absent after the Google
 * callback. A refresh that appears successful can also lose a rotation race;
 * in that case one final NextAuth-to-API exchange is safe and bounded.
 */
export async function resolveApiSessionUser({
  fetchUser,
  refresh,
  exchange,
}: ApiSessionOperations): Promise<ApiSessionResolution> {
  try {
    return { user: await fetchUser(), recovery: null };
  } catch (error) {
    if (!isUnauthorized(error)) throw error;
  }

  const refreshed = await refresh();
  if (refreshed) {
    try {
      return { user: await fetchUser(), recovery: "refresh" };
    } catch (error) {
      if (!isUnauthorized(error)) throw error;
    }
  }

  await exchange();
  return { user: await fetchUser(), recovery: "exchange" };
}

/** Decide what the protected account layout must render without side effects. */
export function resolveAccountGateState({
  sessionStatus,
  hasUser,
  hasError,
}: {
  sessionStatus: "loading" | "authenticated" | "unauthenticated";
  hasUser: boolean;
  hasError: boolean;
}): AccountGateState {
  if (sessionStatus === "loading") return "loading";
  if (sessionStatus === "unauthenticated") return "redirect";
  if (hasUser) return "content";
  if (hasError) return "error";
  return "loading";
}
